import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ScanBarcode, Package, Plus, Camera, Loader2, Star, Scale, Hash } from 'lucide-react';
import { useLanguage, api } from '../../context/AppContext';
import { toast } from 'sonner';

const LabelScannerModal = ({
  open,
  onOpenChange,
  scanning,
  labelResult,
  labelProductName,
  onProductNameChange,
  labelServings,
  onServingsChange,
  onRescan,
  onAdd
}) => {
  const { language } = useLanguage();
  const [savingFav, setSavingFav] = useState(false);
  const [isFav, setIsFav] = useState(false);
  // Toggle: 'grams' or 'portions'
  const [inputMode, setInputMode] = useState('grams');
  const [gramsValue, setGramsValue] = useState('');

  // Initialize grams from serving_size_g when result arrives
  React.useEffect(() => {
    if (labelResult?.serving_size_g) {
      setGramsValue(String(labelResult.serving_size_g));
      onServingsChange(1);
    }
  }, [labelResult?.serving_size_g]);

  const servingSizeG = labelResult?.serving_size_g || 100;
  const ps = labelResult?.per_serving || {};

  // Compute multiplier based on mode
  const multiplier = useMemo(() => {
    if (inputMode === 'grams') {
      const g = parseFloat(gramsValue) || 0;
      return g / servingSizeG;
    }
    return labelServings;
  }, [inputMode, gramsValue, servingSizeG, labelServings]);

  // Computed totals
  const totals = useMemo(() => ({
    protein_g: Math.round((ps.protein_g || 0) * multiplier * 10) / 10,
    calories: Math.round((ps.calories || 0) * multiplier),
    carbs_g: Math.round((ps.carbs_g || 0) * multiplier * 10) / 10,
    fats_g: Math.round((ps.fats_g || 0) * multiplier * 10) / 10,
  }), [ps, multiplier]);

  // Sync servings for parent (used by onAdd)
  const handleGramsChange = (val) => {
    setGramsValue(val);
    const g = parseFloat(val) || 0;
    onServingsChange(g / servingSizeG);
  };

  const handlePortionsChange = (val) => {
    const p = parseFloat(val) || 0;
    onServingsChange(p);
    setGramsValue(String(Math.round(p * servingSizeG)));
  };

  const handleSaveAsFavorite = async () => {
    if (!labelResult?.per_serving) return;
    setSavingFav(true);
    try {
      const productName = labelProductName.trim() || labelResult.product_name || 'Produit';
      await api.post('/foods/favorites', {
        name: productName,
        calories: ps.calories || 0,
        protein_g: ps.protein_g || 0,
        carbs_g: ps.carbs_g || 0,
        fats_g: ps.fats_g || 0,
        fiber_g: ps.fiber_g || 0,
        portion_description: labelResult.serving_size || `${servingSizeG}g`,
      });
      setIsFav(true);
      toast.success(language === 'fr' ? 'Ajoute aux favoris !' : 'Added to favorites!');
    } catch {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    } finally { setSavingFav(false); }
  };

  const fr = language === 'fr';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl uppercase tracking-tight flex items-center gap-2">
            <ScanBarcode className="w-6 h-6 text-chart-carbs" />
            {fr ? 'Scanner une etiquette' : 'Scan Nutrition Label'}
          </DialogTitle>
          <DialogDescription>
            {fr ? "Prenez une photo de l'etiquette nutritionnelle pour extraire les valeurs." : 'Take a photo of the nutrition label to extract values.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {scanning ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-12 h-12 text-chart-carbs animate-spin mb-4" />
              <p className="text-muted-foreground">{fr ? "Analyse de l'etiquette..." : 'Analyzing label...'}</p>
            </div>
          ) : labelResult ? (
            <>
              {/* Product Name */}
              <div className="bg-chart-carbs/10 border border-chart-carbs/30 p-4 rounded-lg">
                <div className="flex items-center gap-3">
                  <Package className="w-8 h-8 text-chart-carbs flex-shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <label className="text-xs text-muted-foreground">{fr ? 'Nom du produit (modifiable)' : 'Product name (editable)'}</label>
                    <Input
                      type="text"
                      value={labelProductName}
                      onChange={(e) => onProductNameChange(e.target.value)}
                      placeholder={fr ? 'Entrez le nom du produit...' : 'Enter product name...'}
                      className="bg-secondary border-chart-carbs/50 text-foreground font-heading"
                      data-testid="label-product-name-input"
                    />
                    {labelResult.serving_size && (
                      <p className="text-[11px] text-muted-foreground">{fr ? 'Portion etiquette' : 'Label serving'}: {labelResult.serving_size} ({servingSizeG}g)</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Quantity Input — Grams / Portions Toggle */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-foreground">
                    {fr ? 'Quantite consommee' : 'Quantity consumed'}
                  </label>
                  {/* Toggle switch */}
                  <div className="flex bg-secondary rounded-lg p-0.5" data-testid="input-mode-toggle">
                    <button
                      onClick={() => setInputMode('grams')}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                        inputMode === 'grams'
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                      data-testid="toggle-grams"
                    >
                      <Scale className="w-3 h-3" />
                      {fr ? 'Grammes / ml' : 'Grams / ml'}
                    </button>
                    <button
                      onClick={() => setInputMode('portions')}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                        inputMode === 'portions'
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                      data-testid="toggle-portions"
                    >
                      <Hash className="w-3 h-3" />
                      Portions
                    </button>
                  </div>
                </div>

                {inputMode === 'grams' ? (
                  <div className="flex items-center gap-3">
                    <Input
                      type="number"
                      value={gramsValue}
                      onChange={(e) => handleGramsChange(e.target.value)}
                      placeholder={fr ? 'Poids en g ou volume en ml' : 'Weight in g or volume in ml'}
                      className="flex-1 h-12 text-lg font-heading text-center bg-secondary border-primary/40"
                      min="0"
                      data-testid="label-grams-input"
                    />
                    <span className="text-sm font-medium text-muted-foreground shrink-0">g/ml</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <Button variant="outline" size="sm" onClick={() => handlePortionsChange(Math.max(0.5, labelServings - 0.5))} className="h-12 w-12 text-lg">-</Button>
                    <Input
                      type="number"
                      value={labelServings}
                      onChange={(e) => handlePortionsChange(e.target.value)}
                      className="flex-1 h-12 text-lg font-heading text-center bg-secondary border-primary/40"
                      min="0.5"
                      step="0.5"
                      data-testid="label-portions-input"
                    />
                    <Button variant="outline" size="sm" onClick={() => handlePortionsChange(labelServings + 0.5)} className="h-12 w-12 text-lg">+</Button>
                  </div>
                )}

                {/* Helper text */}
                <p className="text-[10px] text-muted-foreground text-center">
                  {inputMode === 'grams'
                    ? (fr ? `1 portion = ${servingSizeG}g — Entrez le poids de votre balance` : `1 serving = ${servingSizeG}g — Enter your scale weight`)
                    : (fr ? `1 portion = ${servingSizeG}g` : `1 serving = ${servingSizeG}g`)}
                </p>
              </div>

              {/* Per Serving Reference */}
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground">
                  {fr ? `Par portion (${servingSizeG}g):` : `Per serving (${servingSizeG}g):`}
                </p>
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div className="bg-secondary p-1.5 rounded">
                    <p className="text-[10px] text-muted-foreground">Prot</p>
                    <p className="text-sm font-bold text-primary">{ps.protein_g || 0}g</p>
                  </div>
                  <div className="bg-secondary p-1.5 rounded">
                    <p className="text-[10px] text-muted-foreground">Cal</p>
                    <p className="text-sm font-bold">{ps.calories || 0}</p>
                  </div>
                  <div className="bg-secondary p-1.5 rounded">
                    <p className="text-[10px] text-muted-foreground">Gluc</p>
                    <p className="text-sm font-bold">{ps.carbs_g || 0}g</p>
                  </div>
                  <div className="bg-secondary p-1.5 rounded">
                    <p className="text-[10px] text-muted-foreground">Lip</p>
                    <p className="text-sm font-bold">{ps.fats_g || 0}g</p>
                  </div>
                </div>
              </div>

              {/* TOTAL — Live recalculated */}
              <div className="bg-red-500/10 border-2 border-red-500/30 p-4 rounded-lg" data-testid="label-totals">
                <p className="text-xs font-heading uppercase text-red-400 mb-2">
                  {fr
                    ? `Total (${inputMode === 'grams' ? `${gramsValue || 0}g` : `${labelServings} portion${labelServings > 1 ? 's' : ''}`}):`
                    : `Total (${inputMode === 'grams' ? `${gramsValue || 0}g` : `${labelServings} serving${labelServings > 1 ? 's' : ''}`}):`}
                </p>
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div>
                    <p className="font-heading text-xl text-red-400 font-black" data-testid="total-protein">{totals.protein_g}g</p>
                    <p className="text-[10px] text-muted-foreground">Prot</p>
                  </div>
                  <div>
                    <p className="font-heading text-xl text-red-400 font-black" data-testid="total-calories">{totals.calories}</p>
                    <p className="text-[10px] text-muted-foreground">Cal</p>
                  </div>
                  <div>
                    <p className="font-heading text-xl text-red-400 font-black" data-testid="total-carbs">{totals.carbs_g}g</p>
                    <p className="text-[10px] text-muted-foreground">Gluc</p>
                  </div>
                  <div>
                    <p className="font-heading text-xl text-red-400 font-black" data-testid="total-fats">{totals.fats_g}g</p>
                    <p className="text-[10px] text-muted-foreground">Lip</p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-2">
                <div className="flex gap-3">
                  <Button variant="outline" onClick={onRescan} className="flex-1">
                    <ScanBarcode className="w-4 h-4 mr-2" />
                    {fr ? 'Re-scanner' : 'Re-scan'}
                  </Button>
                  <Button onClick={onAdd} className="flex-1 bg-primary text-primary-foreground" data-testid="add-label-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    {fr ? 'Ajouter' : 'Add'}
                  </Button>
                </div>
                <Button
                  variant="outline"
                  onClick={handleSaveAsFavorite}
                  disabled={savingFav || isFav}
                  className={`w-full ${isFav ? 'border-amber-500/50 text-amber-400' : 'border-amber-500/30 text-amber-400 hover:bg-amber-500/10'}`}
                  data-testid="label-save-favorite-btn"
                >
                  {savingFav ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Star className={`w-4 h-4 mr-2 ${isFav ? 'fill-amber-400' : ''}`} />}
                  {isFav ? (fr ? 'Sauvegarde !' : 'Saved!') : (fr ? 'Ajouter aux favoris' : 'Add to favorites')}
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <ScanBarcode className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-30" />
              <p className="text-muted-foreground mb-4">{fr ? "Prenez une photo de l'etiquette nutritionnelle" : 'Take a photo of the nutrition label'}</p>
              <Button onClick={onRescan} className="bg-chart-carbs text-white hover:bg-chart-carbs/90">
                <Camera className="w-4 h-4 mr-2" />
                {fr ? 'Prendre une photo' : 'Take a photo'}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LabelScannerModal;
